import {
  HoveredEvents,
  PAEventType
} from '@dadohr/shared/domainTypes/productAnalytics';
import {
  EMPTY_TASK_STATUS_COUNTS,
  IReportActor,
  IReportOverviewTasks,
  TaskStatusCounts
} from '@dadohr/shared/domainTypes/reports';
import styled from '@emotion/styled';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { ReportNumberItem } from '../ReportBigNumber';
import { TasksPieChart } from '../TasksPieChart';

const GridNumberContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${(p) => p.theme.spacing(2)}px;
  flex: 1;

  @media (min-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 910px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1110px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1536px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;
const GridItem = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing()}px;
  justify-content: flex-start;
`;
const GridTotalCount = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing()}px;
  width: 18%;
`;

const getRelevantTotal = (x: TaskStatusCounts) =>
  x.completedLate + x.completedOnTime + x.overdue + x.ongoing;

type TrackingInfo = {
  name: HoveredEvents;
  explanation: string;
};

export const TotalTasks = ({
  data,
  actors,
  title
}: {
  data: IReportOverviewTasks;
  actors: IReportActor[];
  title: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const stats: Record<string, TrackingInfo> = {
    tasks: {
      name: 'Tasks Completion By Role Statistics Hovered',
      explanation:
        'Admin user hovered over the pie charts showing task completion rate by role'
    }
  };
  return (
    <FlexContainer
      alignItems="flex-start"
      wrap="wrap"
      spacing={2}
      onMouseEnter={() =>
        Object.keys(stats).forEach((stat: string) => {
          if (stat === title) {
            trackEvent({
              eventType: PAEventType.OTHER,
              name: stats[stat].name,
              Explanation: stats[stat].explanation
            });
          }
        })
      }
    >
      <GridTotalCount>
        <TasksPieChart
          data={[
            { name: t('Not Completed'), value: data.counts.overdue },
            {
              name: t('Completed'),
              value: data.counts.completedOnTime + data.counts.completedLate
            }
          ]}
        />
        <ReportNumberItem
          label={t('Total Sent').toUpperCase()}
          number={getRelevantTotal(data.counts)}
        />
      </GridTotalCount>
      <GridNumberContainer>
        {actors.map((actor) => {
          const counts =
            data.byActor[actor.key]?.counts || EMPTY_TASK_STATUS_COUNTS();
          return (
            <GridItem key={actor.key}>
              <TasksPieChart
                data={[
                  {
                    name: t('Not Completed'),
                    value: counts.overdue || 1 // add a 1 so that the graph shows something
                  },
                  {
                    name: t('Completed'),
                    value: counts.completedOnTime + counts.completedLate
                  }
                ]}
              />
              <ReportNumberItem
                label={actor.label}
                number={getRelevantTotal(counts)}
                color={actor.color}
              />
            </GridItem>
          );
        })}
      </GridNumberContainer>
    </FlexContainer>
  );
};
